/* Pawsome — GSAP animation layer
   Loaded after gsap.min.js, ScrollTrigger.min.js, lenis.min.js, theme.js.
   theme.js falls back to its own IntersectionObserver reveals when GSAP
   is absent; when GSAP is present this file takes over all motion. */
(function () {
  'use strict';

  if (!window.gsap || !window.ScrollTrigger) return;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) {
    document.querySelectorAll('.reveal, .reveal-scale').forEach(function (el) {
      el.classList.add('is-visible');
    });
    var pre = document.querySelector('[data-preloader]');
    if (pre) pre.remove();
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('gsap-on');

  var isTouch = window.matchMedia('(hover: none)').matches;

  /* ------------------------------------------------------------------
     Smooth scrolling (Lenis) driven by the GSAP ticker
     ------------------------------------------------------------------ */
  var lenis = null;
  if (window.Lenis && !isTouch) {
    lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);

    document.querySelectorAll('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = link.getAttribute('href');
        if (href.length < 2) return;
        var target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          lenis.scrollTo(target, { offset: -80 });
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Preloader: paw stamps in, then the curtain lifts
     ------------------------------------------------------------------ */
  // The opening is CSS-driven and lives in theme.js so it does not depend on
  // GSAP loading. Here we only hand control back to the entrance animations.
  function playPreloader(onDone) {
    var pre = document.querySelector('[data-preloader]');
    if (!pre) { onDone(); return; }
    if (pre.classList.contains('is-done')) { onDone(); return; }
    pre.addEventListener('fayar:intro-done', onDone, { once: true });
    setTimeout(onDone, 2600);
  }

  /* ------------------------------------------------------------------
     Hero entrance: title bounces in letter by letter
     ------------------------------------------------------------------ */
  function splitChars(el) {
    var words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    var chars = [];
    words.forEach(function (word) {
      var wordSpan = document.createElement('span');
      wordSpan.className = 'word';
      for (var i = 0; i < word.length; i++) {
        var charSpan = document.createElement('span');
        charSpan.className = 'char';
        charSpan.textContent = word[i];
        wordSpan.appendChild(charSpan);
        chars.push(charSpan);
      }
      el.appendChild(wordSpan);
      el.appendChild(document.createTextNode(' '));
    });
    return chars;
  }

  function heroEntrance() {
    var title = document.querySelector('.hero__title');
    if (!title) return;
    var chars = splitChars(title);

    var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    tl.fromTo('.hero__eyebrow', { y: 26, opacity: 0, scale: 0.8 }, { y: 0, opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(2)' })
      .fromTo(chars,
        { y: 70, opacity: 0, rotation: function () { return gsap.utils.random(-14, 14); } },
        { y: 0, opacity: 1, rotation: 0, duration: 0.7, ease: 'back.out(1.8)', stagger: 0.022 }, '-=0.25')
      .fromTo('.hero__subtext', { y: 26, opacity: 0 }, { y: 0, opacity: 1, duration: 0.55 }, '-=0.45')
      .fromTo('.hero__actions', { y: 26, opacity: 0 }, { y: 0, opacity: 1, duration: 0.55 }, '-=0.4')
      .fromTo('.hero__trust', { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5 }, '-=0.4')
      .fromTo('.hero__media', { scale: 0.6, opacity: 0, rotation: -6 }, { scale: 1, opacity: 1, rotation: 0, duration: 1, ease: 'elastic.out(1, 0.6)' }, 0.35)
      .fromTo('.hero__badge', { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.6, ease: 'back.out(2.4)', stagger: 0.15 }, '-=0.6');

    /* mark hero reveals handled so the batch reveal skips them */
    document.querySelectorAll('.hero .reveal, .hero .reveal-scale').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  /* ------------------------------------------------------------------
     Scroll reveals via ScrollTrigger.batch
     ------------------------------------------------------------------ */
  function initScrollReveals() {
    var targets = Array.prototype.filter.call(
      document.querySelectorAll('.reveal, .reveal-scale'),
      function (el) { return !el.classList.contains('is-visible'); }
    );
    targets.forEach(function (el) {
      gsap.set(el, el.classList.contains('reveal-scale')
        ? { scale: 0.85, opacity: 0 }
        : { y: 44, opacity: 0 });
      el.classList.add('is-visible'); /* neutralise CSS-hidden state; GSAP owns it now */
    });

    ScrollTrigger.batch(targets, {
      start: 'top 88%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, {
          y: 0, scale: 1, opacity: 1,
          duration: 0.85,
          ease: 'power3.out',
          stagger: 0.09,
          overwrite: true
        });
      }
    });

    /* safety: anything skipped by a fast jump gets shown */
    ScrollTrigger.addEventListener('refreshInit', function () {
      targets.forEach(function (el) {
        if (el.getBoundingClientRect().top < 0) gsap.set(el, { y: 0, scale: 1, opacity: 1 });
      });
    });
  }

  /* ------------------------------------------------------------------
     Hero parallax: layers drift apart as you scroll away
     ------------------------------------------------------------------ */
  function initParallax() {
    if (!document.querySelector('.hero')) return;
    var scrub = {
      trigger: '.hero', start: 'top top', end: 'bottom top', scrub: 1
    };
    gsap.to('.hero__image-blob', { yPercent: 18, scrollTrigger: scrub });
    gsap.to('.hero__badge--top', { yPercent: -120, rotation: -8, scrollTrigger: scrub });
    gsap.to('.hero__badge--bottom', { yPercent: 90, rotation: 6, scrollTrigger: scrub });
    gsap.to('.hero__blob--1', { yPercent: 45, scrollTrigger: scrub });
    gsap.to('.hero__blob--2', { yPercent: -35, scrollTrigger: scrub });
    gsap.to('.hero__content', { yPercent: 12, opacity: 0.25, scrollTrigger: scrub });
    gsap.to('.hero__bone', { y: -140, rotation: 60, scrollTrigger: scrub });
    gsap.to('.hero__ball', { y: -190, x: 40, scrollTrigger: scrub });

    /* image-with-text blob floats gently against scroll */
    document.querySelectorAll('.image-text__blob').forEach(function (blob) {
      gsap.fromTo(blob, { y: 60 }, {
        y: -60,
        scrollTrigger: { trigger: blob, start: 'top bottom', end: 'bottom top', scrub: 1.2 }
      });
    });
  }

  /* ------------------------------------------------------------------
     Marquee reacts to scroll velocity (skews like fast wind)
     ------------------------------------------------------------------ */
  function initMarqueeVelocity() {
    var tracks = document.querySelectorAll('.marquee__track');
    if (!tracks.length) return;
    var proxy = { skew: 0 };
    var skewSetters = Array.prototype.map.call(tracks, function (t) {
      return gsap.quickSetter(t, 'skewX', 'deg');
    });
    var clamp = gsap.utils.clamp(-12, 12);
    ScrollTrigger.create({
      onUpdate: function (self) {
        var skew = clamp(self.getVelocity() / -140);
        if (Math.abs(skew) > Math.abs(proxy.skew)) {
          proxy.skew = skew;
          gsap.to(proxy, {
            skew: 0, duration: 0.9, ease: 'power3',
            overwrite: true,
            onUpdate: function () {
              skewSetters.forEach(function (set) { set(proxy.skew); });
            }
          });
        }
      }
    });
  }

  /* ------------------------------------------------------------------
     3D tilt on cards (desktop only)
     ------------------------------------------------------------------ */
  function initTilt() {
    if (isTouch) return;
    var selector = '.category-card, .product-card__media, .testimonial-card, .hero__image-blob';
    document.querySelectorAll(selector).forEach(function (card) {
      var setX = gsap.quickTo(card, 'rotationY', { duration: 0.5, ease: 'power2.out' });
      var setY = gsap.quickTo(card, 'rotationX', { duration: 0.5, ease: 'power2.out' });
      gsap.set(card, { transformPerspective: 700 });

      card.addEventListener('mousemove', function (e) {
        var rect = card.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width - 0.5;
        var py = (e.clientY - rect.top) / rect.height - 0.5;
        setX(px * 14);
        setY(py * -14);
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(card, { rotationX: 0, rotationY: 0, duration: 0.9, ease: 'elastic.out(1, 0.5)' });
      });
    });
  }

  /* ------------------------------------------------------------------
     Magnetic buttons: primary buttons lean toward the cursor
     ------------------------------------------------------------------ */
  function initMagnetic() {
    if (isTouch) return;
    document.querySelectorAll('.btn--primary, .testimonials__controls button, .header-icon').forEach(function (btn) {
      var xTo = gsap.quickTo(btn, 'x', { duration: 0.4, ease: 'power3.out' });
      var yTo = gsap.quickTo(btn, 'y', { duration: 0.4, ease: 'power3.out' });
      btn.addEventListener('mousemove', function (e) {
        var rect = btn.getBoundingClientRect();
        xTo((e.clientX - rect.left - rect.width / 2) * 0.35);
        yTo((e.clientY - rect.top - rect.height / 2) * 0.35);
      });
      btn.addEventListener('mouseleave', function () {
        gsap.to(btn, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.4)' });
      });
    });
  }

  /* ------------------------------------------------------------------
     Paw-print cursor trail
     ------------------------------------------------------------------ */
  function initPawTrail() {
    if (isTouch) return;
    var lastX = -100, lastY = -100, step = 0;
    var MIN_DIST = 110;

    document.addEventListener('mousemove', function (e) {
      var dx = e.clientX - lastX;
      var dy = e.clientY - lastY;
      if (dx * dx + dy * dy < MIN_DIST * MIN_DIST) return;

      var angle = Math.atan2(dy, dx) * 180 / Math.PI + 90;
      /* alternate paws left/right of the movement line, like a real walk */
      step++;
      var side = step % 2 === 0 ? 1 : -1;
      var perp = (angle - 90) * Math.PI / 180;
      var ox = Math.cos(perp + Math.PI / 2) * 10 * side;
      var oy = Math.sin(perp + Math.PI / 2) * 10 * side;

      lastX = e.clientX;
      lastY = e.clientY;

      var paw = document.createElement('span');
      paw.className = 'paw-trail';
      paw.textContent = '🐾';
      paw.style.left = (e.clientX + ox) + 'px';
      paw.style.top = (e.clientY + oy) + 'px';
      paw.style.transform = 'rotate(' + angle + 'deg)';
      document.body.appendChild(paw);

      gsap.fromTo(paw,
        { scale: 0, opacity: 0.4 },
        {
          scale: 1, opacity: 0.28, duration: 0.18, ease: 'back.out(2)',
          onComplete: function () {
            gsap.to(paw, {
              opacity: 0, scale: 0.7, duration: 0.9, delay: 0.25, ease: 'power1.in',
              onComplete: function () { paw.remove(); }
            });
          }
        });
    }, { passive: true });
  }

  /* ------------------------------------------------------------------
     Stat counters
     ------------------------------------------------------------------ */
  function initCounters() {
    document.querySelectorAll('[data-counter]').forEach(function (el) {
      var target = parseInt(el.dataset.target, 10) || 0;
      var obj = { val: 0 };
      gsap.to(obj, {
        val: target,
        duration: 1.8,
        ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 90%', once: true },
        onUpdate: function () {
          el.textContent = Math.round(obj.val).toLocaleString();
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Section headings get a soft zoom-through
     ------------------------------------------------------------------ */
  function initHeadingPop() {
    document.querySelectorAll('.section-heading h2').forEach(function (h) {
      gsap.fromTo(h,
        { scale: 0.92, opacity: 0, y: 30 },
        {
          scale: 1, opacity: 1, y: 0, duration: 0.8, ease: 'back.out(1.6)',
          scrollTrigger: { trigger: h, start: 'top 88%', once: true }
        });
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function start() {
    heroEntrance();
    initScrollReveals();
    initParallax();
    initMarqueeVelocity();
    initTilt();
    initMagnetic();
    initPawTrail();
    initCounters();
    initHeadingPop();
    ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { playPreloader(start); });
  } else {
    playPreloader(start);
  }
})();
